import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import * as endPoints from '@/endpoints'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'
import { summariseDailyUsage, usageWindow } from '../relations'

const EXPLORE = {
    electricity: endPoints.POWER_EXPLORE,
    gas: endPoints.GAS_EXPLORE
}

export const USAGE_DAYS = 30

/** Whether daily usage can be shown for a fuel (water has no readings API). */
export const hasUsage = (fuel) => Boolean(EXPLORE[fuel])

/**
 * Daily kWh over the last 30 days for one or more meters of one fuel, summed
 * by day — a location's electricity is the sum of its electricity meters.
 */
export default function useDailyUsage(fuel, meterIds) {
    const { activeOrg } = useAuth()
    const orgId = activeOrg?.id
    const window = useMemo(() => usageWindow(USAGE_DAYS), [])
    const endpoint = EXPLORE[fuel]

    const results = useQueries({
        queries: (endpoint ? meterIds : []).map((meterId) => ({
            queryKey: [
                'entityPeekUsage',
                orgId,
                fuel,
                meterId,
                window.start,
                window.end
            ],
            queryFn: () =>
                axios
                    .get(`${endpoint}daily/${window.start}/${window.end}/`, {
                        params: { transpose: false, meter_id: meterId }
                    })
                    .then((response) =>
                        Array.isArray(response.data)
                            ? (response.data[0]?.data ?? [])
                            : []
                    ),
            enabled: Boolean(orgId),
            staleTime: 1000 * 60 * 10
        }))
    })

    const isLoading = results.some((result) => result.isLoading)
    const isError =
        results.length > 0 && results.every((result) => result.isError)
    const dataKey = results.map((result) => result.dataUpdatedAt).join()
    const summary = useMemo(
        () =>
            summariseDailyUsage(
                results.flatMap((result) => result.data ?? []),
                window
            ),
        [dataKey, window]
    )

    return { summary, isLoading, isError, window }
}
